
import React, { useEffect, useMemo } from "react";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import "./flowStatusHistory.css";

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const getTimeAgo = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 0) return "Just now";
  if (seconds < 60) return "Just now";

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);

  if (hours < 24) return `${hours} hr ago`;

  const days = Math.floor(hours / 24);

  if (days < 30) return `${days} days ago`;

  const months = Math.floor(days / 30);

  if (months < 12) return `${months} months ago`;

  return `${Math.floor(days / 365)} years ago`;
};

const getStatusDetails = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized.includes("created")) {
    return {
      icon: "bi-plus-circle-fill",
      badge: "badge-created",
    };
  }

  if (normalized.includes("pending")) {
    return {
      icon: "bi-hourglass-split",
      badge: "badge-pending",
    };
  }

  if (
    normalized.includes("reject") ||
    normalized.includes("cancel") ||
    normalized.includes("fail")
  ) {
    return {
      icon: "bi-x-circle-fill",
      badge: "badge-reject",
    };
  }

  if (
    normalized.includes("complete") ||
    normalized.includes("fully loaded") ||
    normalized.includes("delivered")
  ) {
    return {
      icon: "bi-flag-fill",
      badge: "badge-complete",
    };
  }

  if (
    normalized.includes("approve") ||
    normalized.includes("invoice") ||
    normalized.includes("issued")
  ) {
    return {
      icon: "bi-check-circle-fill",
      badge: "badge-approved",
    };
  }

  if (normalized.includes("submit")) {
    return {
      icon: "bi-send-fill",
      badge: "badge-submit",
    };
  }

  return {
    icon: "bi-arrow-right-circle-fill",
    badge: "badge-pending",
  };
};

const FlowStatusHistory = React.forwardRef((props, ref) => {
  const data = props?.rowdatamodal;

  const history = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.flowStatusHistory)) {
      return data.flowStatusHistory;
    }

    return [];
  }, [data]);

  const documentNumber = useMemo(() => {
    if (Array.isArray(data)) {
      return (
        data[0]?.documentNumber ||
        data[0]?.orderSchedulingNr ||
        data[0]?.businessComponentInstanceId ||
        ""
      );
    }

    return (
      data?.documentNumber ||
      data?.orderSchedulingNr ||
      data?.businessComponentInstanceId ||
      data?.orderNumber ||
      ""
    );
  }, [data]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        props.onHide?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
     // eslint-disable-next-line 
  }, [props.onHide]);

  return (
    <Modal
      {...props}
      ref={ref}
      aria-labelledby="flow-status-history-title"
      keyboard={false}
      size="lg"
      centered
      backdrop="static"
      dialogClassName="flow-history-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title id="flow-status-history-title">
          <i className="bi bi-info-square me-2" />
          {props.modeltitle || "Status Information"}
          {documentNumber ? ` of ${documentNumber}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="flow-history-body">
        <Row>
          <Col xs={12}>
            {history.length > 0 ? (
              <div className="timeline">
                {history.map((item, index) => {
                  const status =
                    item?.status || item?.currentAction || "Unknown";

                  const timestamp =
                    item?.at ||
                    item?.updatedAt ||
                    item?.createdAt ||
                    item?.timestamp;

                  const note =
                    item?.note ||
                    item?.remark ||
                    item?.description ||
                    "";

                  const details = getStatusDetails(status);

                  const approvers = Array.isArray(item?.approveresInfo)
                    ? item.approveresInfo
                    : [];

                  const isCompleted =
                    details.badge === "badge-complete" ||
                    details.badge === "badge-approved";

                  return (
                    <div
                      key={
                        item?._id ||
                        `${status}-${timestamp || index}-${index}`
                      }
                      className={`timeline-item ${
                        isCompleted ? "completed" : ""
                      }`}
                    >
                      <div className="timeline-icon">
                        <i className={`bi ${details.icon}`} />
                      </div>

                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span
                            className={`status-badge ${details.badge}`}
                          >
                            {status}
                          </span>

                          <span className="timeline-time">
                            {getTimeAgo(timestamp)}
                          </span>
                        </div>

                        {timestamp && (
                          <div className="timeline-datetime">
                            <i className="bi bi-calendar3 me-1" />
                            {formatDateTime(timestamp)}
                          </div>
                        )}

                        {note && (
                          <div className="timeline-remark">
                            <i className="bi bi-chat-left-text me-1" />
                            {note}
                          </div>
                        )}

                        {approvers.map((approver, approverIndex) => (
                          <div
                            key={
                              approver?._id ||
                              `${approver?.userId}-${approverIndex}`
                            }
                            className="timeline-user"
                          >
                            <div className="fw-bold">
                              {approver?.userId
                                ? `${approver.userId} - `
                                : ""}
                              {approver?.firstName ||
                                approver?.organisationName ||
                                ""}
                            </div>

                            {approver?.email && (
                              <div className="timeline-email">
                                {approver.email}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="timeline-empty">
                <i className="bi bi-clock-history" />
                <p>No workflow history available.</p>
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
});

FlowStatusHistory.displayName = "FlowStatusHistory";

export default FlowStatusHistory;
